'use client';
import classNames from 'classnames/bind';
import omit from 'lodash.omit';
import React, { forwardRef } from 'react';
import AnchorSpanButton from '../common/AnchorSpanButton.mjs';
import LoadingBasic from '../loading/LoadingBasic.mjs';
import { BUTTON_VARIANT, BUTTON_BOX_SIZE } from '../../constants/button.mjs';
import useApplyLogging from '../../hooks/useApplyLogging.mjs';
import { includes } from '../../utils/object.mjs';
import styles from './ButtonBox.module.scss.mjs';

const cx = classNames.bind(styles);
/**
 * @description ### Loading 이미지 Color
 * | variant     | isDark        | color                                                    |
 * | :---------- | :------------ | :------------------------------------------------------- |
 * | primary     |X              | white(fill='#fff' fill2='#fff' fill3='#fff')             |
 * | primary     |O              | black(fill='#000' fill2='#000' fill3='#000')             |
 * | secondary   |X              | 삼색(fill 정의하지 않음)                                      |
 * | secondary   |X(custom color)| white(fill='#fff' fill2='#fff' fill3='#fff')             |
 * | secondary   |O              | black(fill='#000' fill2='#000' fill3='#000')             |
 */
const getLoadingFillsProps = loadingProps => {
  switch (loadingProps.variant) {
    case 'primary':
      {
        return loadingProps.isDark ? {
          fill: '#000',
          fill2: '#000',
          fill3: '#000'
        } : {
          fill: '#fff',
          fill2: '#fff',
          fill3: '#fff'
        };
      }
    case 'secondary':
      {
        if (loadingProps.color === 'custom') {
          return {
            fill: '#000',
            fill2: '#000',
            fill3: '#000'
          };
        }
        return loadingProps.isDark ? {
          fill: '#000',
          fill2: '#000',
          fill3: '#000'
        } : {};
      }
    /** variant=tertiary는 loading이 없음 */
    default:
      {
        return {};
      }
  }
};
/**
 * @description ### Loading 이미지 size
    - Max : 32*8(width=32 height=8)
    - LargeFull : 31*8(width=31 height=8)
    - Large, Medium : 28*7(width=28 height=7)
    - Small, Tiny : 24*6(width=24 height=6)
*/
const getLoadingSize = size => {
  return {
    [BUTTON_BOX_SIZE.MAX]: {
      width: '32px',
      height: '8px'
    },
    [BUTTON_BOX_SIZE.LARGE_FULL]: {
      width: '31px',
      height: '8px'
    },
    [BUTTON_BOX_SIZE.LARGE]: {
      width: '28px',
      height: '7px'
    },
    [BUTTON_BOX_SIZE.MEDIUM]: {
      width: '28px',
      height: '7px'
    },
    [BUTTON_BOX_SIZE.SMALL]: {
      width: '24px',
      height: '6px'
    },
    [BUTTON_BOX_SIZE.TINY]: {
      width: '24px',
      height: '6px'
    }
  }[size];
};
/**
 * HTML 기본 속성이 아닌, 디자인시스템 자체적으로 정의된 속성이 AnchorSpanButton을 통해 HTML 기본태그에 전달되지 않도록 방어처리
 * - TODO: 자동화 또는 타입 에러가 뜰 수 있도록 개선하기
 * @see https://oss.fin.navercorp.com/Product-Planning/DeFign_Plan/issues/1240
 */
function sanitizeNoAnchorSpanButtonProps(props) {
  return omit(props, ['text', 'size', 'variant', 'variantType', 'loading', 'color', 'fixWidth', 'isDark', 'isWide']);
}
/**
 * @description ### ButtonBox (v5)
 * |           | primary | primary | secondary | secondary | tertiary | tertiary |
 * |-----------|---------|---------|-----------|-----------|----------|----------|
 * |           | fill    | line    | fill      | line      | fill     | line     |
 * | max       | O       | -       | O         | -         | -        | -        |
 * | largefull | O       | -       | O         | O         | O        | O        |
 * | large     | O       | -       | O         | O         | O        | O        |
 * | medium    | O       | -       | O         | O         | O        | O        |
 * | small     | O       | -       | O         | O         | O        | O        |
 * | tiny      | O       | -       | O         | O         | O        | O        |
 *
 */
const ButtonBox = /*#__PURE__*/forwardRef((props, ref) => {
  useApplyLogging('ButtonBox');
  const hasLoading = !props.disabled && includes([BUTTON_VARIANT.PRIMARY, BUTTON_VARIANT.SECONDARY], props.variant) && props.loading;
  /**
   * 로딩이 돌고 있을 때 관련 이벤트 자체가 발생하지 않도록 핸들러를 감싸서 제공합니다.
   * @see https://oss.fin.navercorp.com/Product-Planning/DeFign_Plan/issues/1305
   */
  const handleClick = event => {
    var _props$onClick;
    if (hasLoading) {
      event.preventDefault();
      return;
    }
    return (_props$onClick = props.onClick) === null || _props$onClick === void 0 ? void 0 : _props$onClick.call(props, event);
  };
  switch (props.size) {
    case BUTTON_BOX_SIZE.MAX:
      {
        const {
          text,
          size,
          variant,
          color,
          disabled,
          isDark,
          ...anchorSpanButtonProps
        } = props;
        return /*#__PURE__*/React.createElement(AnchorSpanButton, {
          ref: ref,
          className: cx('article', size, variant, 'fill', color && `color-${color}`, {
            'font-dark': isDark
          }, {
            disabled
          }),
          disabled: disabled,
          ...{
            ...sanitizeNoAnchorSpanButtonProps(anchorSpanButtonProps),
            onClick: handleClick
          }
        }, hasLoading ? ( /*#__PURE__*/React.createElement("span", {
          className: cx('loading')
        }, /*#__PURE__*/React.createElement(LoadingBasic, {
          ...getLoadingSize(size),
          ...getLoadingFillsProps({
            ...props
          })
        }))) : ( /*#__PURE__*/React.createElement(React.Fragment, null, text)));
      }
    case BUTTON_BOX_SIZE.LARGE_FULL:
      {
        const {
          text,
          size,
          variant,
          color,
          disabled,
          isDark,
          ...anchorSpanButtonProps
        } = props;
        return /*#__PURE__*/React.createElement(AnchorSpanButton, {
          ref: ref,
          className: cx('article', size, variant, props.variant === 'primary' ? 'fill' : props.variantType ?? 'fill', color && `color-${color}`, {
            'font-dark': isDark
          }, {
            disabled
          }),
          disabled: disabled,
          ...{
            ...sanitizeNoAnchorSpanButtonProps(anchorSpanButtonProps),
            onClick: handleClick
          }
        }, hasLoading ? ( /*#__PURE__*/React.createElement("span", {
          className: cx('loading')
        }, /*#__PURE__*/React.createElement(LoadingBasic, {
          ...getLoadingSize(size),
          ...getLoadingFillsProps({
            ...props
          })
        }))) : ( /*#__PURE__*/React.createElement(React.Fragment, null, text)));
      }
    case BUTTON_BOX_SIZE.LARGE:
    case BUTTON_BOX_SIZE.MEDIUM:
    case BUTTON_BOX_SIZE.SMALL:
    case BUTTON_BOX_SIZE.TINY:
      {
        const {
          text,
          size,
          variant,
          color,
          disabled,
          isWide,
          fixWidth,
          isDark,
          ...anchorSpanButtonProps
        } = props;
        return /*#__PURE__*/React.createElement(AnchorSpanButton, {
          ref: ref,
          className: cx('article', size, variant, props.variant === 'primary' ? 'fill' : props.variantType ?? 'fill', color && `color-${color}`, {
            'font-dark': isDark
          }, {
            disabled
          }, {
            'type-wide': isWide
          }, {
            'padding-reset': fixWidth
          }),
          disabled: disabled,
          style: {
            width: `${fixWidth}px`
          },
          ...{
            ...sanitizeNoAnchorSpanButtonProps(anchorSpanButtonProps),
            onClick: handleClick
          }
        }, hasLoading ? ( /*#__PURE__*/React.createElement("span", {
          className: cx('loading')
        }, /*#__PURE__*/React.createElement(LoadingBasic, {
          ...getLoadingSize(size),
          ...getLoadingFillsProps({
            ...props
          })
        }))) : ( /*#__PURE__*/React.createElement(React.Fragment, null, text)));
      }
  }
});
ButtonBox.displayName = 'ButtonBox';

export { ButtonBox as default };
